import { InsightFilterEnum } from 'appRedux/models/favoritesModels';
import { ChartOptions } from 'chart.js';
import { IGraphData } from 'common/common';
import { parseValue } from 'common/include';

const maxTicksByTimeframe: Record<string, number> = {
  hours1: 4,
  hours4: 4,
  hours8: 8,
  hours24: 8,
  weeks1: 7,
  months1: 8,
};

const allowedAnnotationInsights = [InsightFilterEnum.ReturnToPlayer, InsightFilterEnum.Volatility];

export const getChartOptions = (
  rtpMin: number,
  rtpMax: number,
  popNumber: number,
  timeframe: string,
  y1MinPadded: number,
  y1MaxPadded: number,
  hasAccess: boolean,
  primaryValue?: IGraphData,
  secondaryValue?: IGraphData
): ChartOptions<'bar' | 'line'> => {
  const maxTicks = maxTicksByTimeframe[timeframe] || 10;
  const seenDates = new Set<string>();
  const shouldShowAnnotations =
    hasAccess && allowedAnnotationInsights.includes(primaryValue?.value as InsightFilterEnum);
  const showY1 = secondaryValue?.displayAxesText !== false;

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      intersect: true,
      axis: 'x',
      includeInvisible: true,
    },
    scales: {
      x: {
        title: {
          display: false,
          text: 'Time',
        },
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: function (_val, index, ticks) {
            const label = String(this.getLabelForValue(_val as number) ?? '');
            if (!label) return null;

            const n = ticks.length;

            if (/^\d{1,2}:\d{2}$/.test(label)) {
              const step = Math.max(1, Math.ceil(n / maxTicks));
              if (index % step !== 0) return null;
              const [hourStr, minuteStr] = label.split(':');
              const minute = parseInt(minuteStr, 10);
              if (minute !== 0) return null;
              const hour = parseInt(hourStr, 10);
              const displayHour = hour % 12 === 0 ? 12 : hour % 12;
              return `${displayHour} ${hour < 12 ? 'AM' : 'PM'}`;
            }

            // Date label: deduplicate — never show the same date twice
            if (seenDates.has(label)) return null;
            seenDates.add(label);

            if (timeframe === 'months1') {
              // Step-filter unique dates to ~8 labels
              const uniqueIndex = seenDates.size - 1;
              const step = Math.max(1, Math.ceil(30 / maxTicks));
              if (uniqueIndex % step !== 0) return null;
            }

            return label;
          },
        },
      },
      y: {
        min: rtpMin,
        position: 'left',
        title: {
          display: hasAccess,
          text: primaryValue?.name,
        },
        grid: {
          display: true,
        },
        ticks: {
          display: hasAccess,
          callback: (value) => {
            const isReturnInsightData = primaryValue?.value === InsightFilterEnum.ReturnToPlayer;
            let dataReturn = '';
            if (isReturnInsightData) {
              const datavalue = parseFloat(String(Number(value) / 100));
              dataReturn = parseValue(datavalue, primaryValue?.value);
            } else {
              dataReturn = parseValue(value, primaryValue?.value);
            }
            return dataReturn;
          },
        },
      },
      ...(showY1 && {
        y1: {
          beginAtZero: true,
          position: 'right',
          min: 0,
          max: y1MaxPadded,
          title: {
            display: true,
            text: secondaryValue?.name || '',
          },
          grid: {
            display: false,
          },
          ticks: {
            callback: (value) => {
              if (secondaryValue?.value === InsightFilterEnum.Volatility) {
                const labels = {
                  1: 'L',
                  2: '',
                  3: 'M',
                  4: '',
                  5: '',
                  6: 'H',
                };
                return labels[value as 1 | 2 | 3 | 4 | 5 | 6] || '';
              } else {
                const dataReturn = parseValue(value, secondaryValue?.value);
                return dataReturn;
              }
            },
          },
        },
      }),
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      annotation: {
        annotations:
          hasAccess && shouldShowAnnotations
            ? {
              breakeven: {
                type: 'line',
                yMin: 100,
                yMax: 100,
                borderColor: 'green',
                borderWidth: 2,
                borderDash: [6, 6],
                yScaleID: 'y',
                label: {
                  display: true as const,
                  content: 'Break Even 100%',
                  position: 'end',
                  color: 'green',
                  font: {
                    weight: 'bold',
                    size: 12,
                  },
                  yAdjust: -20,
                  padding: 8,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: 4,
                },
              },
              pop: {
                type: 'line',
                yMin: popNumber,
                yMax: popNumber,
                borderColor: 'orange',
                borderWidth: 2,
                borderDash: [6, 6],
                yScaleID: 'y',
                label: {
                  display: true as const,
                  content: `POP Threshold ${popNumber}%`,
                  position: 'end',
                  color: 'orange',
                  font: {
                    weight: 'bold',
                    size: 12,
                  },
                  yAdjust: 20,
                  padding: 8,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: 4,
                },
              },
            }
            : {},
      },
    },
  };
};
